/**
 * Entity API
 */

import { apiClient } from '@shared/api/client';
import { useSessionStore } from '@/shared/stores/sessionStore';
import type {
  Entity,
  CreateEntityData,
  UpdateEntityData,
  EntityListParams,
  EntityListResponse,
  EntityStats,
  EntityControllerResponse,
  EntityControllerDetail,
} from '../types/entity.types';

/**
 * 세션이 말레이시아 클라이언트인지 확인
 * - Authorization / Entity 공통에서 사용
 */
function isMalaysiaSession(): boolean {
  try {
    const sessionStore = useSessionStore();
    const customerInfo = sessionStore.user?.customer || null;
    const jurisdiction = customerInfo?.jurisdiction || '';
    return jurisdiction?.toUpperCase() === 'MY' || jurisdiction?.toUpperCase() === 'MALAYSIA';
  } catch {
    return false;
  }
}

export const entityApi = {
  /**
   * 엔티티 목록 조회
   * - MY 세션: /entity_my
   * - 그 외:   /entity
   */
  getEntities: (params?: EntityListParams) => {
    const basePath = isMalaysiaSession() ? '/entity_my' : '/entity';
    return apiClient.get<EntityListResponse>(basePath, { params });
  },

    /**
     * 엔티티 기본 정보 조회 (EntityName 섹션용)
     */
    getEntityById: (id: string) => {
        return apiClient.get<Entity>(`/entity/${id}`);
    },

    /**
     * 엔티티 Profile 탭 데이터 조회 (Info + Main Contact)
     */
    getEntityProfile: (id: string) => {
        return apiClient.get<any>(`/entity/profile/${id}`);
    },

    /**
     * 엔티티 Officer 탭 데이터 조회
     */
    getEntityOfficer: (id: string) => {
        return apiClient.get<any>(`/entity/officer/${id}`);
    },

    /**
     * 엔티티 Officer 포지션 목록 조회
     */
    getEntityOfficerPositions: (id: string) => {
        return apiClient.get<any>(`/entity/officer/${id}/positions`);
    },

    /**
     * 엔티티 Officer 상세 조회
     */
    getEntityOfficerDetail: (id: string, officerIdx: number) => {
        return apiClient.get<any>(`/entity/officer/${id}/detail/${officerIdx}`);
    },

    /**
     * 엔티티 Officer 수정
     */
    updateEntityOfficer: (id: string, data: {
        officerIdx: number;
        positionCode: string;
        appointmentDate: string;
        cessationDate?: string | null;
        nomineeDirectorCode?: string | null;
        occupation?: string;
        designation?: string;
        otherDirectorship?: string;
        formerNames?: string;
        isMeeting?: number | null;
        note?: string;
        officerJsonData?: Record<string, unknown>;
    }) => {
        return apiClient.put<any>(`/entity/officer/${id}/detail`, data);
    },

    /**
     * 엔티티 Officer 삭제
     */
    deleteEntityOfficer: (id: string, officerIdx: number) => {
        return apiClient.delete<any>(`/entity/officer/${id}/detail/${officerIdx}`);
    },

    /**
     * Nominee 의 노미네이터 상세 (Officer / Shares 탭 Nominee 뱃지)
     */
    getEntityNominator: (id: string, nominatorContactCd: string, type: 'director' | 'shareholder') => {
        return apiClient.get<any>(`/entity/${id}/nominator/${encodeURIComponent(nominatorContactCd)}`, { params: { type } });
    },

    /**
     * 엔티티 Nominee(Director / Shareholder) ↔ 노미네이터 목록 (챗봇 Nominator 역할)
     */
    getEntityNominators: (id: string) => {
        return apiClient.get<any>(`/entity/${id}/nominators`);
    },

    /**
     * 엔티티 Shares 탭 데이터 조회
     */
    getEntityShares: (id: string) => {
        return apiClient.get<any>(`/entity/shares/${id}`);
    },

    /**
     * 엔티티 Shares 탭 Shareholder 선택용 연락처 목록 조회
     */
    getEntityShareContacts: (id: string) => {
        return apiClient.get<any>(`/entity/shares/${id}/contacts`);
    },

    /**
     * 엔티티 Shareholder 추가
     */
    addEntityShareholder: (id: string, data: {
        shareholder: string;
        transactionDate: string;
        currency: string;
        classOfShares: string;
        numberOfShares: number;
        issuedShareCapital: number;
        paidUpShareCapital: number;
        sharesPayable?: string;
        meetingStatus?: string;
        certificateType?: string;
        certificateNo?: string;
        chairPerson?: string;
        nomineeShareholdersCode?: string | null;
        /** 노미네이터 입력값 (bizfile 원본 필드명) — 백엔드가 L_NOMINATOR 에 저장·GUID 연결 */
        nominator?: Record<string, any> | null;
        transactionType?: string | null;
    }) => {
        return apiClient.post<any>(`/entity/shares/${id}`, data);
    },

    /**
     * 엔티티 Shareholder 디테일 조회
     */
    getEntityShareholderDetail: (id: string, shareCapitalIdx: number, shareholderIdx: number) => {
        return apiClient.get<any>(`/entity/shares/${id}/detail/${shareCapitalIdx}/${shareholderIdx}`);
    },

    /**
     * 엔티티 Shareholder 수정
     */
    updateEntityShareholder: (id: string, shareCapitalIdx: number, shareholderIdx: number, data: {
        shareholder: string;
        transactionDate: string;
        currency: string;
        classOfShares: string;
        numberOfShares: number;
        issuedShareCapital: number;
        paidUpShareCapital: number;
        sharesPayable?: string;
        meetingStatus?: string;
        certificateType?: string;
        certificateNo?: string;
        chairPerson?: string;
        nomineeShareholdersCode?: string | null;
        nominator?: Record<string, any> | null;
        transactionType?: string | null;
        shareClass?: Array<{
            subClassIdx?: number | null;
            typeOfShare?: string;
            ordinary?: number;
            preference?: number;
            others?: number;
            numberOfShares?: number;
            paidUpCapital?: number;
            currency?: string;
        }>;
    }) => {
        return apiClient.put<any>(`/entity/shares/${id}/${shareCapitalIdx}/${shareholderIdx}`, data);
    },

    /**
     * 엔티티 Controller 탭 데이터 조회
     */
    getEntityController: (id: string) => {
        return apiClient.get<EntityControllerResponse>(`/entity/controller/${id}`);
    },

    /**
     * 엔티티 Controller 상세 조회
     */
    getEntityControllerDetail: (id: string, params: { ctrlIdx: string | number; ctrlDetailIdx?: string | number; registerType?: string }) => {
        return apiClient.get<EntityControllerDetail>(`/entity/controller/${id}/detail`, { params });
    },

    /**
     * 엔티티 생성
     */
    createEntity: (data: CreateEntityData) => {
        return apiClient.post<Entity>('/entity/profile', data);
    },

    /**
     * 엔티티 수정
     */
    updateEntity: (id: string, data: UpdateEntityData) => {
        return apiClient.put<Entity>(`/entity/${id}`, data);
    },

    /**
     * 엔티티 삭제
     */
    deleteEntity: (id: string) => {
        return apiClient.delete(`/entity/${id}`);
    },

    /**
     * 엔티티 상태 변경
     */
    updateEntityStatus: (id: string, status: string) => {
        return apiClient.patch(`/entity/${id}/status`, { status });
    },

    /**
     * 엔티티 통계 조회
     */
    getEntityStats: () => {
        return apiClient.get<EntityStats>('/entity/stats');
    },

    /**
     * SSIC 목록 조회
     */
    getSSICList: () => {
        return apiClient.get<any>('/entity/profile/ssic/list');
    },

    /**
     * Entity Type 목록 조회 (A04, SG)
     */
    getEntityTypes: () => {
        return apiClient.get<any>('/entity/profile/entity-types');
    },

    /**
     * Login Users 목록 조회 (Account Manager용)
     */
    getUsersList: () => {
        return apiClient.get<any>('/entity/profile/users/list');
    },

    /**
     * Global Contact 목록 조회 (Main Contact 선택용)
     */
    getContactsList: () => {
        return apiClient.get<any>('/entity/profile/contacts/list');
    },

    /**
     * 엔티티에 Contact 추가 (InsertRecord 규칙 적용)
     */
    entityContactInsertRecord: (entityCd: string, contactCd: string) => {
        return apiClient.post<any>(`/entity/profile/${entityCd}/contact`, {
            contactCd: contactCd
        });
    },

    /**
     * 엔티티에서 Contact 삭제 (DeleteRecord 규칙 적용)
     */
    entityContactDeleteRecord: (entityCd: string, contactCd: string) => {
        return apiClient.delete<any>(`/entity/profile/${entityCd}/contact/${contactCd}`);
    },

    /**
     * Postal Code로 주소 검색
     */
    searchPostalCode: (postalCode: string) => {
        return apiClient.get<{
            blockHouseNo?: string;
            streetName: string;
            buildingEstateName?: string;
        }>('/entity/profile/search-postal-code', {
            params: { postalCode },
        });
    },

    /**
     * 엔티티를 비활성화 (IS_ACTIVE = '0')
     */
    setEntityInactive: (id: string) => {
        return apiClient.patch<any>(`/entity/profile/${id}/inactive`);
    },

    /**
     * 엔티티를 활성화 (IS_ACTIVE = '1')
     */
    setEntityActive: (id: string) => {
        return apiClient.patch<any>(`/entity/profile/${id}/active`);
    },

    /**
     * 엔티티 업데이트 (UpdateRecord 규칙 적용)
     */
    entityUpdateRecord: (id: string, data: any) => {
        console.log('📤 [entityApi.entityUpdateRecord] API 호출:', {
            id,
            hasAlternateAddress: !!data.alternateAddress,
            alternateAddress: data.alternateAddress,
            dataKeys: Object.keys(data),
            fullData: JSON.parse(JSON.stringify(data))
        });
        return apiClient.patch<any>(`/entity/profile/${id}`, data);
    },

    /**
     * Registration Number 중복 체크
     * @param regNo - 확인할 Registration Number
     * @param isValidation - Save 시 validation 체크인지 여부 (true면 VALID001 반환, false면 REG001 반환)
     */
    checkRegistrationNumber: (regNo: string, isValidation: boolean = false, entityCd?: string) => {
        const params: any = { 
            regNo,
            validation: isValidation ? 'true' : undefined
        };
        if (entityCd) {
            params.entityCd = entityCd;
        }
        return apiClient.get<any>('/entity/profile/check-registration-number', {
            params
        });
    },

    /**
     * Entity Create 시 validation 체크
     * @param checkType - 체크 타입 ('duplicate-check' 등)
     */
    validateEntityCreate: (checkType: string = 'duplicate-check') => {
        return apiClient.get<any>('/entity/profile/validate-entity-create', {
            params: { checkType }
        });
    },

    /**
     * 엔티티 Officer 수동 추가
     */
    addEntityOfficer: (id: string, data: { positionCode: string; contactCd?: string; appointmentDate: string; contactRegNo?: string; contactName?: string; nomineeDirectorCode?: string; altDirectorContactCd?: string; nominator?: Record<string, any> | null }) => {
        return apiClient.post<any>(`/entity/officer/${id}`, data);
    },

    /**
     * 엔티티 Officer 연락처 옵션 조회
     */
    getEntityOfficerContacts: (id: string) => {
        return apiClient.get<any>(`/entity/officer/${id}/contacts`);
    },

    /**
     * 엔티티 Controller 여권 만료일만 갱신 (목록 인라인 편집) — L_CONTROLLERMANAGE.PASSPORT_NO_EXPIRY_DATE
     */
    updateEntityControllerPassportExpiry: (id: string, data: { ctrlIdx: number | string; passportNoExpiryDate: string }) => {
        return apiClient.put(`/entity/controller/${id}/passport-expiry`, data);
    },

    /**
     * 엔티티 Controller 옵션 조회 (B09, B07 공통코드)
     */
    getEntityControllerOptions: (id: string) => {
        return apiClient.get<any>(`/entity/controller/${id}/options`);
    },

    /**
     * 엔티티 Controller 추가
     */
    addEntityController: (id: string, data: {
        fullName: string;
        contactCd?: string;
        dateOfAppointed?: string;
        dateOfCessation?: string;
        dateOfEntry?: string;
        dateOfNoticeSent?: string;
        dateOfConfirmation?: string;
        cessationReason?: string;
        confirmationController?: string;
        requireControllerSignature?: string;
        categoryCode: string;
        representativeCd?: string;
        acrAuen?: string;
        nonAcrAuen?: string;
        legalForm?: string;
        jurisdictionScraping?: string;
        jurisdiction?: string;
        incorporationStatute?: string;
        corporateEntityRegister?: string;
        nationalityScraping?: string;
        nationalityCd?: string;
        identificationNo?: string;
        aliases?: string;
        dateOfBirth?: string;
        passportNoExpiryDate?: string;
        addressTypeCd: string;
        postalCode?: string;
        blockNo?: string;
        streetName?: string;
        buildingName?: string;
        addrLevel?: string;
        addrUnit?: string;
        foreignAddressLine1?: string;
        foreignAddressLine2?: string;
        contactEmail?: string;
        countryCd?: string;
        areaCode?: string;
        mobileNo?: string;
        remarks?: string;
    }) => {
        return apiClient.post<any>(`/entity/controller/${id}`, data);
    },

    /**
     * 엔티티 Controller 수정
     */
    updateEntityController: (id: string, data: {
        ctrlIdx: number;
        fullName: string;
        dateOfAppointed?: string;
        dateOfCessation?: string;
        dateOfEntry?: string;
        dateOfNoticeSent?: string;
        dateOfConfirmation?: string;
        cessationReason?: string;
        confirmationController?: string;
        requireControllerSignature?: string;
        categoryCode: string;
        representativeCd?: string;
        acrAuen?: string;
        nonAcrAuen?: string;
        legalForm?: string;
        jurisdictionScraping?: string;
        jurisdiction?: string;
        incorporationStatute?: string;
        corporateEntityRegister?: string;
        nationalityScraping?: string;
        nationalityCd?: string;
        identificationNo?: string;
        aliases?: string;
        dateOfBirth?: string;
        passportNoExpiryDate?: string;
        addressTypeCd: string;
        postalCode?: string;
        blockNo?: string;
        streetName?: string;
        buildingName?: string;
        addrLevel?: string;
        addrUnit?: string;
        foreignAddressLine1?: string;
        foreignAddressLine2?: string;
        contactEmail?: string;
        countryCd?: string;
        areaCode?: string;
        mobileNo?: string;
        remarks?: string;
    }) => {
        return apiClient.put<any>(`/entity/controller/${id}/detail`, data);
    },

    /**
     * 엔티티 Controller 삭제
     */
    deleteEntityController: (id: string, ctrlIdx?: string, ctrlDetailIdx?: string) => {
        const params = new URLSearchParams();
        if (ctrlIdx) params.append('ctrlIdx', ctrlIdx);
        if (ctrlDetailIdx) params.append('ctrlDetailIdx', ctrlDetailIdx);
        const queryString = params.toString();
        return apiClient.delete<any>(`/entity/controller/${id}/detail${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * 엔티티 Shareholder 삭제
     */
    deleteEntityShareholder: (id: string, shareCapitalIdx: number, shareholderIdx: number) => {
        return apiClient.delete<any>(`/entity/shares/${id}/${shareCapitalIdx}/${shareholderIdx}`);
    },

    /**
     * 클라이언트 정보 조회 (CLIENT_CD로 L_CLIENTS 테이블 조회)
     * @param clientCd - 클라이언트 코드 (세션의 customer.code)
     */
    getClientInfo: (clientCd: string) => {
        return apiClient.get<{
            CLIENT_NM: string;
            REG_NO: string;
        }>(`/entity/client-info/${clientCd}`);
    },

    /**
     * 동기화 상태 확인 (L_DAEMON_SYNC_BIZFILE_QUEUE 테이블에서 CLIENT_CD로 status 조회)
     * CLIENT_CD는 세션에서 자동으로 가져옴
     */
    checkSyncStatus: () => {
        return apiClient.get<{
            hasActiveSync: boolean;
            status: number;
        }>(`/entity/sync-status`);
    },

    /**
     * Officer를 레지스터에 등록
     */
    registerOfficer: (entityCd: string, data: {
        contactCd: string;
        positionCode: string;
        appointmentDate: string;
        registerType?: string;
        directorCd?: string; // Alternate Director의 경우 Principal Director의 contactCd
    }) => {
        // positionCode에 따라 적절한 레지스터 엔드포인트로 라우팅
        // 주의: 더 구체적인 조건을 먼저 체크해야 함 (예: NOMINEE DIRECTOR는 DIRECTOR보다 먼저)
        const positionName = data.positionCode?.trim().toUpperCase() || '';
        
        if (positionName.includes('NOMINEE DIRECTOR')) {
            // Nominee Director인 경우 (DIRECTOR보다 먼저 체크)
            return apiClient.post<any>('/registerfromcontact/nomineeDirector', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('ALTERNATE DIRECTOR') || (data as any).directorCd || (data as any)?.alternateDirector?.directorCd) {
            // Alternate Director인 경우 (DIRECTOR보다 먼저 체크)
            const directorCd = ((data as any).directorCd || (data as any)?.alternateDirector?.directorCd || '').toString().trim();
            if (!directorCd) {
                return Promise.reject(new Error('alternateDirector.directorCd is required'));
            }
            return apiClient.post<any>('/registerfromcontact/alternate-director', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType,
                alternateDirector: {
                    directorCd
                }
            });
        }
        
        if (positionName.includes('DIRECTOR')) {
            // Director인 경우
            return apiClient.post<any>('/registerfromcontact/director', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('OWNER')) {
            // Owner인 경우
            return apiClient.post<any>('/registerfromcontact/owner', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('SECRETARY')) {
            // Secretary인 경우
            return apiClient.post<any>('/registerfromcontact/secretary', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('CHIEF EXECUTIVE OFFICER') || positionName.includes('CEO')) {
            // CEO인 경우
            return apiClient.post<any>('/registerfromcontact/ceo', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('AUDITOR')) {
            // Auditor인 경우
            return apiClient.post<any>('/registerfromcontact/auditor', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        if (positionName.includes('DATA PROTECTION OFFICER') || positionName.includes('DPO') || positionName.includes('DATA PROTECTION')) {
            // Data Protection Officer인 경우
            return apiClient.post<any>('/registerfromcontact/dpo', {
                entityCd,
                contactCd: data.contactCd,
                appointmentDate: data.appointmentDate,
                registerType: data.registerType
            });
        }
        
        // 기타 레지스터 타입은 나중에 추가
        return Promise.reject(new Error(`Unsupported register type: ${data.positionCode}`));
    },

    /**
     * Address를 레지스터에 등록
     */
    registerAddress: (entityCd: string, data: {
        addressData: {
            addressTypeCd?: string;
            postalCode?: string;
            blockNo?: string;
            streetName?: string;
            buildingName?: string;
            addrLevel?: string;
            addrUnit?: string;
            foreignAddressLine1?: string;
            foreignAddressLine2?: string;
        };
        dateOfEffective?: string;
        note?: string;
        contactIdUen?: string;
        contactName?: string;
    }) => {
        return apiClient.post<any>('/registerfromcontact/address', {
            entityCd,
            addressData: data.addressData,
            dateOfEffective: data.dateOfEffective,
            note: data.note,
            contactIdUen: data.contactIdUen || '',
            contactName: data.contactName || ''
        });
    },

    /**
     * Allotment를 레지스터에 등록
     */
    registerAllotment: (entityCd: string, data: {
        contactCd: string;
        transactionDate: string;
        certificateNo?: string;
        certificateType?: string;
        classOfShares: string;
        currency: string;
        subClass?: string;
        numberOfShares?: number;
        issuedShareCapital?: number;
        paidUpShareCapital?: number;
        registerType?: string;
        nomineeShareholdersCode?: string | null;
    }) => {
        return apiClient.post<any>('/registerfromcontact/allotment', {
            entityCd,
            contactCd: data.contactCd,
            transactionDate: data.transactionDate,
            certificateNo: data.certificateNo,
            certificateType: data.certificateType,
            classOfShares: data.classOfShares,
            currency: data.currency,
            subClass: data.subClass,
            numberOfShares: data.numberOfShares,
            issuedShareCapital: data.issuedShareCapital,
            paidUpShareCapital: data.paidUpShareCapital,
            registerType: data.registerType,
            nomineeShareholdersCode: data.nomineeShareholdersCode
        });
    },

    /**
     * Controller 레지스터 등록
     */
    registerController: (entityCd: string, data: {
        contactCd: string;
        dateOfEntry?: string;
        dateOfAppointed?: string;
        dateOfCessation?: string;
        cessationReason?: string;
        aliases?: string;
        legalForm?: string;
        jurisdiction?: string;
        incorporationStatute?: string;
        corporateEntityRegister?: string;
        nonAcrAuen?: string;
        dateOfConfirmation?: string;
        categoryCode?: string;
        registerType?: string;
    }) => {
        return apiClient.post<any>('/registerfromcontact/controller', {
            entityCd,
            contactCd: data.contactCd,
            dateOfEntry: data.dateOfEntry,
            dateOfAppointed: data.dateOfAppointed,
            dateOfCessation: data.dateOfCessation,
            cessationReason: data.cessationReason,
            aliases: data.aliases,
            legalForm: data.legalForm,
            jurisdiction: data.jurisdiction,
            incorporationStatute: data.incorporationStatute,
            corporateEntityRegister: data.corporateEntityRegister,
            nonAcrAuen: data.nonAcrAuen,
            dateOfConfirmation: data.dateOfConfirmation,
            categoryCode: data.categoryCode,
            registerType: data.registerType
        });
    },

    // ── SG Entity 싱크 변경 이력 (Change History) — MY 와 동일 UX ──
    /** 해당 entity 의 싱크 변경 이력 (회차별, 최신순) */
    getEntitySyncHistory: (entityCd: string) => {
        return apiClient.get<any>(`/entity/sync-history/${encodeURIComponent(entityCd)}`);
    },
    /** 여러 entity 의 변경 여부 배치 조회 (리스트 아이콘 색 결정용) → { changes, conflicts } */
    getEntitySyncChangeFlags: (entityCds: string[]) => {
        return apiClient.post<any>('/entity/sync-history/change-flags', { entityCds });
    },
    /** 세션 클라이언트의 변경 있는 entityCd 전체 (리스트 "Change History" 필터용) */
    getChangedEntitiesAll: () => {
        return apiClient.get<any>('/entity/sync-history/changed-entities/all');
    },
    /**
     * SG Shares 탭 Sync Changes 숨김 — 최신 회차 Capital/Shareholder detail IS_HIDE=1
     * (Change History 모달에는 영향 없음)
     */
    hideSharesSyncChanges: (entityCd: string) => {
        return apiClient.post<any>(
            `/entity/sync-history/${encodeURIComponent(entityCd)}/hide-shares-sync`,
            {}
        );
    },
};

